/**
 * plannerStart.ts — pure logic behind the planner's first screen.
 *
 *  • buildStartStops: "Where are you starting?" + optional end / loop → the
 *    trip's first stops.
 *  • isLoopTrip: a trip that finishes where it started (Furkot-style round
 *    trip). New stops go between the two ends, never past the finish.
 *  • routeTripFacts: km / days / nights for a popular-trip card, read from the
 *    catalogue's free-text "971 km" / "12-14 hours" / "5-7 days" strings.
 */

import type { TripStop } from '../../types/trip';
import { isLoopTrip } from '../../utils/tripInsert';

export interface StartPlace {
  label: string;
  lat: number;
  lng: number;
  entityId?: number;
}

export interface StartPlanInput {
  start: StartPlace;
  end?: StartPlace | null;
  loop?: boolean;
}

const stopFrom = (place: StartPlace, kind: TripStop['kind'], id: string): TripStop => ({
  id,
  name: place.label,
  kind,
  entityId: place.entityId,
  lat: place.lat,
  lng: place.lng,
  nights: 0,
  tags: [],
  constraints: [],
  locked: false,
});

/**
 * The stops a "Start planning" press creates. A loop wins over an end point
 * (the checkbox hides the end field, but a stale value can still be held).
 * An end that is the same place as the start is treated as a loop.
 */
export function buildStartStops(input: StartPlanInput, makeId: () => string): TripStop[] {
  const start = stopFrom(input.start, 'start', makeId());
  if (input.loop) {
    return [start, stopFrom(input.start, 'end', makeId())];
  }
  if (input.end) {
    return [start, stopFrom(input.end, 'end', makeId())];
  }
  return [start];
}

export { isLoopTrip };

export interface PopularRouteLike {
  distance?: string;
  duration?: string;
}

export interface RouteTripFacts {
  km: number | null;
  days: number | null;
  nights: number | null;
}

/** Hours behind the wheel a day before a caravan trip stops being fun. */
export const COMFORTABLE_DRIVE_HOURS_PER_DAY = 4.5;

function parseRange(text: string): { lo: number; hi: number } | null {
  const m = text.replace(/,/g, '').match(/(\d+(?:\.\d+)?)(?:\s*[-–]\s*(\d+(?:\.\d+)?))?/);
  if (!m) return null;
  const lo = Number(m[1]);
  const hi = m[2] ? Number(m[2]) : lo;
  return Number.isFinite(lo) && Number.isFinite(hi) ? { lo, hi } : null;
}

/**
 * Card facts for a catalogue route. "N days" durations are the trip length as
 * written (lower bound). "N hours" durations are total driving time, spread at
 * a comfortable ~4.5 h a day. Nights = days − 1 (the last day ends at home).
 */
export function routeTripFacts(route: PopularRouteLike): RouteTripFacts {
  const dist = route.distance ? parseRange(route.distance) : null;
  const km = dist ? Math.round(dist.lo) : null;

  let days: number | null = null;
  const dur = route.duration?.toLowerCase() ?? '';
  const range = dur ? parseRange(dur) : null;
  if (range) {
    if (/day/.test(dur)) {
      days = Math.max(1, Math.round(range.lo));
    } else if (/h(ou)?r/.test(dur)) {
      days = Math.max(1, Math.ceil(range.hi / COMFORTABLE_DRIVE_HOURS_PER_DAY));
    }
  }
  return { km, days, nights: days === null ? null : days - 1 };
}

/** "971 km · 3 days · 2 nights" — or "Day trip" for a one-dayer. */
export function formatRouteFacts(facts: RouteTripFacts): string {
  const parts: string[] = [];
  if (facts.km !== null) parts.push(`${facts.km.toLocaleString('en-AU')} km`);
  if (facts.days !== null) {
    if (facts.days <= 1) {
      parts.push('Day trip');
    } else {
      parts.push(`${facts.days} days`);
      if (facts.nights !== null) parts.push(`${facts.nights} night${facts.nights === 1 ? '' : 's'}`);
    }
  }
  return parts.join(' · ');
}

/** Match ALL tokens (connector words ignored) so "Perth to Esperance" finds
 *  "South West Edge" (from Perth, to Esperance). */
export function filterRoutes<T extends { name: string; from?: string; to?: string }>(
  routes: T[],
  query: string
): T[] {
  const tokens = query
    .trim()
    .toLowerCase()
    .replace(/[→>]+/g, ' ')
    .split(/\s+/)
    .filter((t) => t && !['to', 'and', 'via', '-'].includes(t));
  if (!tokens.length) return routes;
  return routes.filter((r) => {
    const hay = `${r.name} ${r.from || ''} ${r.to || ''}`.toLowerCase();
    return tokens.every((t) => hay.includes(t));
  });
}

/**
 * A round trip with nothing between its two ends yet ("Melbourne → back to
 * Melbourne"). There is no drive to show: the ladder renders the start, a
 * "Where to next?" prompt and the way home, never a 0.0 km leg.
 */
export function isUnstartedLoop(stops: TripStop[]): boolean {
  return stops.length === 2 && isLoopTrip(stops);
}

/**
 * Places on the trip, as people count them: the drive home of a round trip
 * is not another stop. Every "N stops" label (panel header, ladder, map chip)
 * reads from this so they can never disagree.
 */
export function tripPlaceCount(stops: TripStop[]): number {
  return isLoopTrip(stops) ? stops.length - 1 : stops.length;
}

/** Leg figures this small are the same place twice: don't print "0.0 km". */
export function isMeaningfulLeg(distanceKm?: number | null): boolean {
  return typeof distanceKm === 'number' && Number.isFinite(distanceKm) && distanceKm >= 0.05;
}

/**
 * What a stop is doing on the trip, in words, for the line under its name:
 * "Start", "2 nights here", "Passing through", "Back home" (the end of a
 * round trip) or "Finish". One 13px sentence-case style for every role — the
 * badge colour carries the rest.
 */
export function stopRoleText(
  role: 'start' | 'via' | 'overnight' | 'end',
  nights: number,
  opts: { homeEnd?: boolean } = {}
): string {
  switch (role) {
    case 'start':
      return 'Start';
    case 'end':
      return opts.homeEnd ? 'Back home' : 'Finish';
    case 'overnight': {
      const n = Math.max(1, Math.round(nights));
      return `${n} night${n === 1 ? '' : 's'} here`;
    }
    default:
      return 'Passing through';
  }
}

/**
 * The one-line trip settings summary: "Car · Leaving Fri 3 Jan · 6 h driving a day".
 * Missing pieces are left out, never shown as blanks.
 */
export function tripSettingsSummary(opts: {
  vehicleLabel?: string | null;
  startDateLabel?: string | null;
  driveHours?: number | null;
}): string {
  const parts: string[] = [];
  if (opts.vehicleLabel) parts.push(opts.vehicleLabel);
  parts.push(opts.startDateLabel ? `Leaving ${opts.startDateLabel}` : 'Any date');
  if (typeof opts.driveHours === 'number' && opts.driveHours > 0) parts.push(`${opts.driveHours} h a day`);
  return parts.join(' · ');
}

/** Near enough to a trip's places to be worth listing before there's a route. */
export const NEAR_TRIP_KM = 250;

/**
 * Before a trip has a route, the campsite list under the planner should be
 * about the trip, not the country: campsites within `maxKm` of any of the
 * trip's places, nearest first. A Melbourne trip used to open over
 * "Engoordina Ruins, NT". Entities without coordinates drop out; with no
 * located stops the list comes back unchanged.
 */
export function nearTripPlaces<T>(
  entities: T[],
  stops: { lat?: number; lng?: number }[],
  coordsOf: (e: T) => { lat: number; lng: number } | null,
  distanceKm: (aLat: number, aLng: number, bLat: number, bLng: number) => number,
  maxKm: number = NEAR_TRIP_KM
): T[] {
  const pts = stops.filter(
    (s): s is { lat: number; lng: number } => typeof s.lat === 'number' && typeof s.lng === 'number'
  );
  if (!pts.length) return entities;
  const scored: { e: T; d: number; i: number }[] = [];
  entities.forEach((e, i) => {
    const c = coordsOf(e);
    if (!c || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) return;
    let best = Infinity;
    for (const p of pts) best = Math.min(best, distanceKm(p.lat, p.lng, c.lat, c.lng));
    if (best <= maxKm) scored.push({ e, d: best, i });
  });
  scored.sort((a, b) => a.d - b.d || a.i - b.i);
  return scored.map((x) => x.e);
}

// ── Popular-trip photos ─────────────────────────────────────────────────────

/**
 * Filename words that mark a poor card hero: archival or bad-weather shots,
 * interiors, wildlife close-ups and historic studio prints. The catalogue's
 * first photo is sometimes one of these ("Gold Coast … in poor weather,
 * 1988") — a recognisable landscape further down the list reads better.
 */
const WEAK_HERO =
  /poor[_ %20-]*weather|interior|robin|penitentiary|anson[_ %20-]*brothers|\b1[89]\d\d\b|_1[89]\d\d[_.)]/i;

const isHttpUrl = (u: unknown): u is string => typeof u === 'string' && /^https?:\/\//.test(u);

/** The best card hero from a route's photos (or undefined when none). */
export function pickHeroImage(urls?: readonly unknown[] | null): string | undefined {
  const usable = (urls ?? []).filter(isHttpUrl);
  let decoded: string[];
  try {
    decoded = usable.map((u) => decodeURIComponent(u));
  } catch {
    decoded = usable;
  }
  const strong = usable.find((_, i) => !WEAK_HERO.test(decoded[i]));
  return strong ?? usable[0];
}

/**
 * Wikimedia thumbnails come at 960px; a 260px card needs a fraction of that.
 * Ask for one of Wikimedia's standard thumbnail widths (others can 429), so a
 * row of cards loads in ~40 KB each instead of ~200 KB. Other hosts pass
 * through unchanged.
 */
const WIKIMEDIA_STEPS = [250, 330, 500, 960, 1280];
export function cardImageUrl(url: string, width = 500): string {
  const m = url.match(/^(https:\/\/upload\.wikimedia\.org\/wikipedia\/commons\/thumb\/.+\/)(\d+)px-([^/]+)$/);
  if (!m) return url;
  const step = WIKIMEDIA_STEPS.find((w) => w >= width) ?? WIKIMEDIA_STEPS[WIKIMEDIA_STEPS.length - 1];
  if (step >= Number(m[2])) return url;
  return `${m[1]}${step}px-${m[3]}`;
}

// ── Quick starts ────────────────────────────────────────────────────────────

/** One-tap starting points under the start field (capital-city centres). */
export const QUICK_STARTS: ReadonlyArray<{ label: string; lat: number; lng: number }> = [
  { label: 'Melbourne', lat: -37.8136, lng: 144.9631 },
  { label: 'Sydney', lat: -33.8688, lng: 151.2093 },
  { label: 'Brisbane', lat: -27.4698, lng: 153.0251 },
  { label: 'Perth', lat: -31.9523, lng: 115.8613 },
  { label: 'Adelaide', lat: -34.9285, lng: 138.6007 },
];

// ── Camps near the start ────────────────────────────────────────────────────

/**
 * "4 km from Melbourne": how far a camp is from where the trip starts, in
 * the words a traveller uses ("pin" was our word, not theirs).
 */
export function distanceFromStartLabel(km: number, startName: string): string {
  const shown = km < 1 ? '<1' : String(Math.round(km));
  const name = startName.split(',')[0].trim() || 'your start';
  return `${shown} km from ${name}`;
}

/**
 * Keep the first camp for each photo and drop later ones that would show the
 * same picture: a "nearest camps" list with the same campfire photo twice
 * reads as fake. Camps without a photo of their own (null) are kept.
 */
export function dedupeByPhoto<T>(items: T[], photoOf: (item: T) => string | null | undefined): T[] {
  const seen = new Set<string>();
  return items.filter((it) => {
    const raw = photoOf(it);
    if (!isHttpUrl(raw)) return true;
    // Same file at different sizes/hosts → same key.
    const key = raw.split('?')[0].replace(/\/\d+px-/, '/').split('/').pop()!.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** A real photo URL — not the credit text ("Manual", "OSM feed") some
 *  map points carry in their image field. */
export const hasPhotoUrl = (u: unknown): boolean => isHttpUrl(u);

// ── One name for the trip ───────────────────────────────────────────────────

/**
 * The trip's name, said the same way in the panel header and the results bar:
 * "Melbourne → Adelaide", "Melbourne round trip", or "From Melbourne" while
 * it's just a start. The bar adds the stop count ("· 6 stops") only once
 * there's somewhere to go — "1 trip stop" for an origin read as a mistake.
 */
export function tripHeadline(stops: TripStop[]): { title: string; barLabel: string; places: number } {
  const places = tripPlaceCount(stops);
  if (!stops.length) return { title: 'Plan a road trip', barLabel: 'Plan a road trip', places: 0 };
  const short = (n?: string) => (n || 'Start').trim();
  const first = short(stops[0]?.name);
  const last = short(stops[stops.length - 1]?.name);
  const title =
    isLoopTrip(stops) || isUnstartedLoop(stops)
      ? `${first} round trip`
      : stops.length === 1
        ? `From ${first}`
        : `${first} → ${last}`;
  const barLabel = places >= 2 ? `${title} · ${places} stops` : title;
  return { title, barLabel, places };
}

// ── A taken trip delivers the nights its card promised ──────────────────────

type LatLng = { lat: number; lng: number };

/**
 * Which middle stops become the nights of a taken trip ("4 days · 3 nights"
 * → three overnights). Picks the stops closest to even shares of the drive
 * (by straight-line distance along the stops), never the start or the
 * finish, never the same stop twice. Stops without coordinates are skipped.
 */
export function pickOvernightIndexes(points: ReadonlyArray<LatLng | null>, nights: number, distKm: (a: LatLng, b: LatLng) => number): number[] {
  const n = points.length;
  if (n < 3 || nights <= 0) return [];
  const cum: number[] = [0];
  let last: LatLng | null = points[0];
  for (let i = 1; i < n; i++) {
    const p = points[i];
    cum.push(cum[i - 1] + (p && last ? distKm(last, p) : 0));
    if (p) last = p;
  }
  const total = cum[n - 1];
  const middles = Array.from({ length: n - 2 }, (_, k) => k + 1).filter((i) => points[i]);
  const want = Math.min(nights, middles.length);
  if (!want) return [];
  const chosen = new Set<number>();
  for (let k = 1; k <= want; k++) {
    const target = total > 0 ? (total * k) / (want + 1) : k;
    let best = -1;
    let bestGap = Infinity;
    for (const i of middles) {
      if (chosen.has(i)) continue;
      const gap = total > 0 ? Math.abs(cum[i] - target) : Math.abs(i - target);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    }
    if (best >= 0) chosen.add(best);
  }
  return Array.from(chosen).sort((a, b) => a - b);
}

/** Nearest camp to a point, within `maxKm`. */
export function nearestCamp<T>(
  at: LatLng,
  camps: ReadonlyArray<T>,
  locate: (c: T) => LatLng | null,
  distKm: (a: LatLng, b: LatLng) => number,
  maxKm = 25
): { camp: T; km: number } | null {
  let best: { camp: T; km: number } | null = null;
  for (const c of camps) {
    const p = locate(c);
    if (!p) continue;
    const km = distKm(at, p);
    if (km <= maxKm && (!best || km < best.km)) best = { camp: c, km };
  }
  return best;
}

/** A leg long enough to be worth breaking up: over the day's driving budget
 *  (default 5h), or 450 km when there's no drive time. */
export function isLongLeg(leg: { distanceKm?: number | null; driveMinutes?: number | null }, maxDriveMinutes = 300): boolean {
  if (typeof leg.driveMinutes === 'number' && leg.driveMinutes > 0) return leg.driveMinutes > maxDriveMinutes;
  return typeof leg.distanceKm === 'number' && leg.distanceKm > 450;
}
